import { toHighlightRuns, type HighlightRun } from "@/lib/fuzzy";
import { searchWorkspace, type WorkspaceSearchSpanDto } from "@/lib/api";
import { resolveEnvironmentFileIdByPath } from "@/lib/environment-files";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import { useSearchStore } from "@/stores/useSearchStore";
import { useToastStore } from "@/stores/useToastStore";

/** 自動検索 (入力中のデバウンス発火) を開始するまでの待機時間。 */
export const SEARCH_DEBOUNCE_MS = 300;
/** 自動検索を発火する最小文字数。これ未満は Enter キーによる明示実行のみ受け付ける。 */
export const MIN_AUTO_SEARCH_QUERY_LENGTH = 2;

let searchGeneration = 0;

/**
 * 検索結果をクリアし、進行中の検索レスポンスを無効化する。`searchGeneration` を進めずに
 * clearResults() だけを呼ぶと、クリア後に解決した古い generation のレスポンスが
 * generation チェックを素通りして結果を復活させてしまうため、必ずこちらを使う。
 */
export function invalidateSearch(): void {
	searchGeneration++;
	useSearchStore.getState().clearResults();
}

/**
 * チャットの切り替え・新規下書き化で検索状態を丸ごと初期化する際に使う。`useSearchStore.reset()`
 * をそのまま呼ぶと `searchGeneration` が進まず、invalidateSearch() と同じ理由で切替前チャットの
 * 古いレスポンスが切替後チャットの検索結果を上書きしてしまうため、必ずこちらを使う。
 */
export function resetSearch(): void {
	searchGeneration++;
	useSearchStore.getState().reset();
}

/**
 * 検索ペインを閉じ、結果もクリアした上で進行中の検索レスポンスを無効化する。
 * `useSearchStore.closeSearch()` をそのまま呼ぶと `searchGeneration` が進まず、閉じている間に
 * 解決した古いレスポンスが再度開いたときの結果を上書きしてしまうため、必ずこちらを使う。
 */
export function closeWorkspaceSearch(): void {
	searchGeneration++;
	useSearchStore.getState().closeSearch();
}

/** useSearchStore の現在のクエリでワークスペース検索を実行し、結果 (または失敗時はエラー) をストアへ反映する。 */
export async function runWorkspaceSearch(environmentId: string): Promise<void> {
	const generation = ++searchGeneration;
	const state = useSearchStore.getState();
	const query = state.query;
	if (!query.trim()) {
		state.setError("検索語を入力してください。");
		return;
	}
	state.setLoading();
	try {
		const result = await searchWorkspace(environmentId, query, state.caseSensitive);
		if (
			generation !== searchGeneration ||
			selectEffectiveEnvironmentId(useChatStore.getState()) !== environmentId
		)
			return;
		useSearchStore.getState().setResults(result.files, result.matchCount, result.truncated);
	} catch (err: unknown) {
		if (generation !== searchGeneration) return;
		useSearchStore
			.getState()
			.setError(err instanceof Error ? err.message : "検索できませんでした。");
	}
}

/**
 * 検索結果の相対パスをファイル ID へ解決する。開いているタブと読み込み済みディレクトリに
 * 無ければサーバーへ問い合わせ、解決できなければトーストで通知する。
 */
export async function resolveFileIdByPath(path: string): Promise<string | null> {
	const environmentId = selectEffectiveEnvironmentId(useChatStore.getState());
	try {
		const fileId = await resolveEnvironmentFileIdByPath(path);
		if (fileId) return fileId;
	} catch (err) {
		console.error("resolveFileIdByPath failed", err);
	}
	// 解決中に環境が切り替わっていたら、切替後の表示に無関係なエラーを持ち込まない。
	if (selectEffectiveEnvironmentId(useChatStore.getState()) !== environmentId) return null;
	useToastStore.getState().show({ type: "error", message: "ファイルが見つかりません。" });
	return null;
}

/** ripgrep が返す (重複しうる) span 群を、ハイライト用の文字インデックス集合として {@link toHighlightRuns} に渡す。 */
export function spansToHighlightRuns(
	text: string,
	spans: readonly WorkspaceSearchSpanDto[],
): HighlightRun[] {
	const indices: number[] = [];
	for (const span of spans) {
		const start = Math.max(0, span.start);
		const end = Math.min(text.length, span.end);
		for (let i = start; i < end; i++) indices.push(i);
	}
	return toHighlightRuns(text, indices);
}
